import { ReactNode } from 'react';

import { Typography as Text } from '@material-ui/core';

import { AttachmentFragment } from '@spotify/confidence-graphql';

import { createModifierClickHandler } from '../../artifactUtils';
import { useArtifactDisplay } from '../../hooks/useArtifactDisplay';
import { ArtifactActionsMenu } from '../ArtifactActionsMenu';
import { ArtifactIcon } from '../ArtifactIcon';
import { ArtifactListItem } from '../ArtifactListItem';

interface DefaultArtifactListItemProps {
  artifact: AttachmentFragment;
  resolvedDisplayName?: string;
  icon?: ReactNode;
  href?: string;
  onSelect: () => void;
  onNavigateToResource?: () => void;
  onDelete?: () => void;
}

export const DefaultArtifactListItem = ({
  artifact,
  resolvedDisplayName,
  icon,
  href,
  onSelect,
  onNavigateToResource,
  onDelete,
}: DefaultArtifactListItemProps) => {
  const { Icon, typeLabel, displayName } = useArtifactDisplay(
    artifact,
    resolvedDisplayName,
  );
  const dateLabel = new Date(artifact.createTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <ArtifactListItem.Root onClick={createModifierClickHandler(href, onSelect)}>
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon ?? <Icon size={16} />}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body1" component="div" noWrap>
            {displayName}
          </Text>
        }
        secondary={
          <Text variant="caption" component="div" color="textSecondary" noWrap>
            {typeLabel} &middot; {dateLabel}
          </Text>
        }
      />
      {(onDelete || onNavigateToResource) && (
        <ArtifactListItem.Action>
          <ArtifactActionsMenu
            artifact={artifact}
            resolvedDisplayName={resolvedDisplayName}
            onNavigateToResource={onNavigateToResource}
            navigateLabel={
              onNavigateToResource ? `Go to ${typeLabel}` : undefined
            }
            onDelete={onDelete}
          />
        </ArtifactListItem.Action>
      )}
    </ArtifactListItem.Root>
  );
};
